const { saveRiotRecordSession } = require("./riotAccountSession");
const { isTransientNetworkError, readWithRetry, parseSdkResponse, decodeJson } = require("./riotRequestRecovery");
const { retryRiotAuthorization, riotAuthorizationFailureCode } = require("./riotRenewalPolicy");

const AUTHORIZATION_URL = "https://auth.riotgames.com/api/v1/authorization";
const ENTITLEMENTS_URL = "https://entitlements.auth.riotgames.com/api/token/v1";
const USERINFO_URL = "https://auth.riotgames.com/userinfo";
const DEFAULT_TIMEOUT_MS = 20000;
const RIOT_COOKIE_NAMES = [
    "asid",
    "tdid",
    "ssid",
    "clid",
    "sub",
    "csid",
    "authenticator.sid",
    "__cflb",
    "__cf_bm",
];
const RECORD_SESSION_PREFIX = "valorant-record:riot:session:";

function requiredText(value, errorCode) {
    if (typeof value !== "string" || !value.trim()) throw new Error(errorCode);
    return value.trim();
}

function readStoredValue(storage, key) {
    let raw;
    try {
        raw = storage.getItem(key);
    } catch (error) {
        throw new Error("RIOT_STORAGE_UNAVAILABLE");
    }
    if (raw === null || raw === undefined) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed && parsed.data ? parsed.data : parsed;
    } catch (error) {
        throw new Error("RIOT_STORAGE_INVALID");
    }
}

function parseAccessToken(uri) {
    const rawUri = requiredText(uri, "RIOT_TOKEN_INVALID");
    const hashIndex = rawUri.indexOf("#");
    const queryIndex = rawUri.indexOf("?");
    const paramsText = hashIndex >= 0
        ? rawUri.slice(hashIndex + 1)
        : (queryIndex >= 0 ? rawUri.slice(queryIndex + 1) : rawUri);
    const token = new URLSearchParams(paramsText).get("access_token");
    return requiredText(token, "RIOT_TOKEN_INVALID");
}

function cookieValues(cookieText) {
    const values = new Map();
    const text = String(cookieText || "");
    for (const name of RIOT_COOKIE_NAMES) {
        const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const match = text.match(new RegExp(`(?:^|[;,]\\s*)${escapedName}=([^;,\\s]+)`, "i"));
        if (match) values.set(name, match[1]);
    }
    return values;
}

function mergeRiotCookie(previousCookie, responseHeaders) {
    const values = cookieValues(previousCookie);
    const headerParams = responseHeaders && Array.isArray(responseHeaders.headerParams)
        ? responseHeaders.headerParams : [];
    for (const header of headerParams) {
        if (!header || String(header.key || "").toLowerCase() !== "set-cookie") continue;
        for (const [name, value] of cookieValues(header.value)) values.set(name, value);
    }
    return RIOT_COOKIE_NAMES
        .filter((name) => values.has(name))
        .map((name) => `${name}=${values.get(name)};`)
        .join("");
}

function sendRiotRequest(gsAppJsApi, url, headers, body, timeoutMs, attempt, stage) {
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (callback, value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            attempt.cancellations.delete(cancel);
            callback(value);
        };
        const cancel = () => finish(reject, new Error("RIOT_LOGIN_OBSOLETE"));
        const timer = setTimeout(() => finish(reject, Object.assign(new Error("RIOT_RENEW_TIMEOUT"), { transientNetwork: true })), timeoutMs);
        attempt.cancellations.add(cancel);
        try {
            attempt.assertCurrent();
            const dispatched = gsAppJsApi.Net.send(url, "POST", "application/json", headers, body, (response, responseHeaders) => {
                if (settled) return;
                try {
                    attempt.assertCurrent();
                    response = parseSdkResponse(url, headers, response, responseHeaders).data;
                    if (stage === "ENTITLEMENTS") requiredText(response.entitlements_token, "RIOT_ENTITLEMENTS_INVALID");
                    if (stage === "USERINFO" && requiredText(response.sub, "RIOT_USERINFO_INVALID") !== attempt.puuid) throw new Error("RIOT_ACCOUNT_MISMATCH");
                    finish(resolve, { response, responseHeaders });
                } catch (error) {
                    const denied = stage === "AUTHORIZATION" && riotAuthorizationFailureCode(decodeJson(response));
                    if (stage === "AUTHORIZATION" && (error.httpStatus === 401
                        || (denied && [0, 200, 403].includes(Number(error.httpStatus) || 0)))) {
                        finish(reject, Object.assign(new Error(denied || "RIOT_COOKIE_EXPIRED"), { httpStatus: error.httpStatus }));
                    } else if (stage === "AUTHORIZATION" && error.code === "RIOT_RESPONSE_INVALID"
                        && !error.businessFailure && (!error.httpStatus || error.httpStatus === 200)) {
                        finish(reject, new Error("RIOT_AUTHORIZATION_INVALID"));
                    } else finish(reject, error);
                }
            });
            if (dispatched === false) finish(reject, new Error("RIOT_RENEW_UNAVAILABLE"));
        } catch (error) {
            finish(reject, error.message === "RIOT_LOGIN_OBSOLETE" ? error
                : Object.assign(new Error("RIOT_RENEW_REQUEST_FAILED"), { transientNetwork: isTransientNetworkError(error) }));
        }
    });
}

async function renewRiotSessionWithCookie(gsAppJsApi, storage, expectedPuuid, timeoutMs = DEFAULT_TIMEOUT_MS, isCurrent) {
    const puuid = requiredText(expectedPuuid, "RIOT_SESSION_NOT_READY");
    const assertCurrent = () => {
        if (isCurrent && !isCurrent()) throw new Error("RIOT_LOGIN_OBSOLETE");
    };
    assertCurrent();
    if (!gsAppJsApi || !gsAppJsApi.Net || typeof gsAppJsApi.Net.send !== "function") {
        throw new Error("RIOT_RENEW_UNAVAILABLE");
    }
    const account = readStoredValue(storage, RECORD_SESSION_PREFIX + puuid);
    const storedPuuid = account && typeof account.sub === "string" ? account.sub.trim() : "";
    if (storedPuuid && storedPuuid !== puuid) throw new Error("RIOT_ACCOUNT_MISMATCH");
    const previousCookie = requiredText(account && account.HeadersCookie, "RIOT_COOKIE_NOT_FOUND");
    let deadline = Date.now() + timeoutMs;
    const remainingTime = () => Math.max(1, deadline - Date.now());
    const attempt = { puuid, finished: false, cancellations: new Set(), assertCurrent() {
        assertCurrent();
        if (this.finished) throw new Error("RIOT_LOGIN_OBSOLETE");
        if (Date.now() >= deadline) throw new Error("RIOT_RENEW_TIMEOUT");
    } };

    try {
        const authorization = await retryRiotAuthorization(async () => {
            deadline = Date.now() + timeoutMs;
            const result = await sendRiotRequest(gsAppJsApi, AUTHORIZATION_URL, {
                Cookie: previousCookie,
            }, {
                response_type: "token id_token",
                redirect_uri: "https://playvalorant.com/opt_in",
                scope: "openid link ban lol_region account",
                nonce: "02afv5lo4o9h",
                client_id: "play-valorant-web-prod",
            }, remainingTime(), attempt, "AUTHORIZATION");
            const type = result.response && result.response.type;
            if (type === "auth") throw new Error("RIOT_COOKIE_EXPIRED");
            if (type === "multifactor") throw new Error("RIOT_LOGIN_INTERACTION_REQUIRED");
            if (type !== "response") throw new Error("RIOT_AUTHORIZATION_INVALID");
            try {
                parseAccessToken(result.response.response && result.response.response.parameters
                    && result.response.response.parameters.uri);
            } catch (_) { throw new Error("RIOT_AUTHORIZATION_INVALID"); }
            return result;
        }, assertCurrent);
        const accessToken = parseAccessToken(
            authorization.response.response
            && authorization.response.response.parameters
            && authorization.response.response.parameters.uri
        );
        const refreshedCookie = mergeRiotCookie(previousCookie, authorization.responseHeaders) || previousCookie;

        const sendToken = (url, stage) => readWithRetry(() => sendRiotRequest(gsAppJsApi, url, {
            Authorization: `Bearer ${decodeURIComponent(accessToken)}`,
        }, {}, remainingTime(), attempt, stage), { check: () => attempt.assertCurrent(), now: () => Date.now(), deadline, retryDelayMs: 0 });
        const [entitlements, userinfo] = await Promise.all([
            sendToken(ENTITLEMENTS_URL, "ENTITLEMENTS"), sendToken(USERINFO_URL, "USERINFO"),
        ]);
        const entitlementsToken = requiredText(
            entitlements.response && entitlements.response.entitlements_token,
            "RIOT_ENTITLEMENTS_INVALID"
        );
        const returnedPuuid = requiredText(userinfo.response && userinfo.response.sub, "RIOT_USERINFO_INVALID");
        if (returnedPuuid !== puuid) throw new Error("RIOT_ACCOUNT_MISMATCH");
        assertCurrent();

        const session = {
            puuid,
            accessToken,
            entitlementsToken,
            loginMode: (account && account.loginMode) || "ApiMode",
            headersCookie: refreshedCookie,
        };
        saveRiotRecordSession(storage, session);
        return session;
    } finally {
        attempt.finished = true;
        for (const cancel of Array.from(attempt.cancellations)) cancel();
    }
}

module.exports = {
    mergeRiotCookie,
    parseAccessToken,
    renewRiotSessionWithCookie,
};
